import React, { useState, useEffect, useCallback } from 'react';
import API from '@scale/frontend-lib/dashboard-api';
import { Typography, Button, Modal, Banner } from '@scale/scaleui';
import { CircularProgress } from 'react-scale/components/design-system';
import { AuditorPermissionSelect } from '@scale/shared/taskerScreening/types';
import SelectScreeningReviewAuditPermissionRoles from 'react-scale/corp/components/SelectScreeningReviewAuditPermissionRoles';

interface TaskerScreeningBenchmarkAssignPermissionsModalProps {
  benchmarkId: string;
  open: boolean;
  onClose: () => void;
}

const PaddedHeader = ({ title }: { title: string }) => {
  return (
    <div className="mt-4 mb-2">
      <Typography.H5>{title}</Typography.H5>
    </div>
  );
};

const TaskerScreeningBenchmarkAssignPermissionsContents: React.FC<
  TaskerScreeningBenchmarkAssignPermissionsModalProps
> = ({ benchmarkId, onClose }) => {
  const [permissionRoleSelects, setPermissionRoleSelects] = useState<AuditorPermissionSelect[]>([]);
  const [loadingFetch, setLoadingFetch] = useState<boolean>(false);

  const handleFetchBenchmarkPermissions = useCallback(async (benchmarkId: string) => {
    setLoadingFetch(true);
    const { permissionRoleSelects } = await API.genai.fetchScreeningBenchmarkPermissionsInfo({
      id: benchmarkId,
    });
    setPermissionRoleSelects(permissionRoleSelects);
    setLoadingFetch(false);
  }, []);

  useEffect(() => {
    handleFetchBenchmarkPermissions(benchmarkId);
  }, [benchmarkId, handleFetchBenchmarkPermissions]);

  const updateBenchmarkPermissions = async (id: string, permissionRoles: string[]) => {
    await API.genai.updateBenchmarkPermissions({
      id,
      permissionRoles,
    });
  };

  const handleUpdateBenchmarkPermissions = useCallback(
    async (id: string, permissionRoleSelects: AuditorPermissionSelect[]) => {
      const permissionRoles = permissionRoleSelects.map(p => p.value);
      await updateBenchmarkPermissions(id, permissionRoles);
      onClose();
    },
    [onClose],
  );

  return (
    <div className="scale-ui scale-theme scale-tailwind w-[600px]">
      <PaddedHeader title="Benchmark ID" />
      <Typography.Paragraph2>{benchmarkId}</Typography.Paragraph2>
      {loadingFetch ? (
        <div className="mt-4">
          <CircularProgress />
        </div>
      ) : (
        <div>
          <PaddedHeader title="Auditor Permission Roles" />
          <SelectScreeningReviewAuditPermissionRoles
            value={permissionRoleSelects}
            onChange={setPermissionRoleSelects}
          />
          <Banner
            className="mb-2 mt-8"
            title="Benchmarks and Permissions"
            subtitle="Auditors are served benchmarks that have corresponding auditor permission roles assigned. If an auditor hasn't passed the benchmark wall for the permission (if one is set), their audit table will be populated only with benchmarks. Once they pass the benchmark wall, benchmarks will be shuffled into their table."
            variant="info"
            background="neutral"
          />
          <Button
            className="mt-8"
            variant="primary"
            onClick={() => handleUpdateBenchmarkPermissions(benchmarkId, permissionRoleSelects)}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

const TaskerScreeningBenchmarkAssignPermissionsModal: React.FC<
  TaskerScreeningBenchmarkAssignPermissionsModalProps
> = ({ open, onClose, benchmarkId }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      theme="scale-theme"
      title="Audit Permission Benchmark Settings"
      innerClassName="overflow-y-auto"
    >
      <TaskerScreeningBenchmarkAssignPermissionsContents
        open={open}
        onClose={onClose}
        benchmarkId={benchmarkId}
      />
    </Modal>
  );
};

export default TaskerScreeningBenchmarkAssignPermissionsModal;
